/**
 * Service Worker Registration — Sippar PWA
 *
 * Registra el service worker generado por CRA + Workbox InjectManifest.
 * Gestiona actualizaciones y la comunicación con la UI.
 */

// Tipos para las opciones de registro
interface Config {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
}

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  window.location.hostname === '[::1]' ||
  window.location.hostname.match(
    /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
  )
);

/**
 * Registrar el service worker
 */
export function register(config?: Config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    // El service worker solo funciona con HTTPS (o localhost)
    const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // PUBLIC_URL está en un origin diferente → el SW no funcionará
      return;
    }

    // ─── Auto-recarga al activarse un SW nuevo ────────────────────────────
    // El service worker hace skipWaiting()+clientsClaim(), así que el SW nuevo
    // toma el control enseguida, pero la página abierta seguiría mostrando el
    // build antiguo (precacheado) hasta un refresco manual. Recargamos al
    // detectar el cambio de controlador para servir la versión nueva.
    // Guarda: si NO había un SW controlando la página al cargar, es la primera
    // instalación (clientsClaim inicial) y no se debe recargar.
    const hadControllerAtLoad = Boolean(navigator.serviceWorker.controller);
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!hadControllerAtLoad || refreshing) return;
      refreshing = true;
      window.location.reload();
    });

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        // En localhost: verificar que el SW existe y registrar
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then(() => {
          console.log(
            '[SW] App served cache-first by service worker. ' +
            'See https://cra.link/PWA'
          );
        });
      } else {
        // En producción: registrar directamente
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) return;

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // Nuevo contenido disponible — el SW anterior sigue sirviendo
              // hasta que todos los tabs se cierren
              console.log(
                '[SW] Nuevo contenido disponible. Se activará cuando se cierren todas las pestañas. ' +
                'Consulta https://cra.link/PWA'
              );

              // Notificar a la app
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // Todo precacheado por primera vez
              console.log('[SW] Contenido cacheado para uso offline.');

              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('[SW] Error durante el registro:', error);
    });
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  // Verificar que el service worker existe realmente
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      // Si el SW no se encuentra o devuelve HTML (404), desregistrar
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // SW encontrado — registrar normalmente
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('[SW] No hay conexión a internet. La app funciona en modo offline.');
    });
}

/**
 * Desregistrar el service worker
 */
export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error('[SW] Error al desregistrar:', error);
      });
  }
}
